import React from 'react';
import { useI18n } from '../../i18n/I18nContext';
import { formatDateTime } from '../../utils/datetime';
import { initialsOf, avatarTone } from '../Avatar/Avatar';
import { formatWhenShort } from './when';
import './MessageConversationList.css';

/**
 * The inbox's conversation list: one `.ui-list-row` per conversation (avatar, name, last
 * message, time, unread count). The row is the shared list row; this file only adds what is
 * specific to a conversation. The open one is marked with `aria-current` and `is-active`.
 */
function MessageConversationList({ conversations, activeId, onSelectConversation }) {
  const { t } = useI18n();

  const previewOf = (conversation) => {
    const last = conversation.lastMessage;
    const text = typeof last === 'string' ? last : (last?.body || last?.content || last?.text || '');
    return text || t('messaging.noMessagesPreview');
  };

  return (
    <ul className="mcl-list">
      {conversations.map((conversation, index) => {
        const isActive = conversation.id === activeId;
        const unread = conversation.unreadCount || 0;
        const name = conversation.participantName || t('messaging.unknownUser');
        return (
          <li key={conversation.id || conversation.participantId || `conversation-${index}`}>
            <button
              type="button"
              className={[
                'ui-list-row',
                'mcl-item',
                isActive ? 'is-active' : '',
                unread ? 'mcl-item--unread' : '',
              ].filter(Boolean).join(' ')}
              aria-current={isActive ? 'true' : undefined}
              onClick={() => onSelectConversation(conversation)}
            >
              <span className="ui-avatar" data-tone={avatarTone(conversation.id || name)} aria-hidden="true">
                {initialsOf(name)}
                {conversation.isOnline && <span className="ui-presence" />}
              </span>

              <span className="ui-list-row__body">
                <span className="ui-list-row__title">{name}</span>
                <span className="ui-list-row__sub">{previewOf(conversation)}</span>
              </span>

              <span className="mcl-side">
                <span
                  className="ui-list-row__meta mcl-time"
                  title={conversation.lastMessageAt ? formatDateTime(conversation.lastMessageAt) : undefined}
                >
                  {formatWhenShort(conversation.lastMessageAt, t)}
                </span>
                {unread > 0 && (
                  <span className="ui-count mcl-badge">
                    <span aria-hidden="true">{unread > 99 ? '99+' : unread}</span>
                    <span className="visually-hidden">{t('messaging.unread', { count: unread })}</span>
                  </span>
                )}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default MessageConversationList;
